import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';

// --- Types ---

type MaintenanceStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
type MaintenancePriority = 'low' | 'medium' | 'high' | 'urgent';
type MaintenanceCategory = 'plumbing' | 'electrical' | 'structural' | 'painting' | 'appliance' | 'general' | 'other';

interface MaintenanceRequest {
  id: number;
  property_id: number;
  property_title?: string;
  title: string;
  description: string;
  category: MaintenanceCategory;
  priority: MaintenancePriority;
  status: MaintenanceStatus;
  reporter_name?: string;
  reporter_source?: 'tenant' | 'company';
  assigned_to_user_id?: number | null;
  assigned_to_name?: string | null;
  estimated_cost?: number | null;
  actual_cost?: number | null;
  resolution_notes?: string | null;
  created_at: string;
}

interface MaintenanceListResponse {
  data: MaintenanceRequest[];
  meta?: {
    total: number;
    page: number;
    page_size: number;
  };
}

interface PropertyOption {
  id: number;
  title: string;
}

interface PropertiesResponse {
  data: PropertyOption[];
}

interface AgentOption {
  id: number;
  first_name: string;
  last_name: string;
}

interface AgentsResponse {
  data: AgentOption[];
}

// --- Constants ---

const CATEGORY_LABEL: Record<MaintenanceCategory, string> = {
  plumbing: 'Plomeria',
  electrical: 'Electricidad',
  structural: 'Estructura',
  painting: 'Pintura',
  appliance: 'Electrodomesticos',
  general: 'General',
  other: 'Otro',
};

const CATEGORY_OPTIONS: MaintenanceCategory[] = [
  'plumbing', 'electrical', 'structural', 'painting', 'appliance', 'general', 'other',
];

const STATUS_LABEL: Record<MaintenanceStatus, string> = {
  open: 'Abierta',
  in_progress: 'En progreso',
  resolved: 'Resuelta',
  closed: 'Cerrada',
};

const STATUS_BADGE: Record<MaintenanceStatus, string> = {
  open: 'badge-warning',
  in_progress: 'badge-info',
  resolved: 'badge-success',
  closed: 'badge-ghost',
};

const PRIORITY_LABEL: Record<MaintenancePriority, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  urgent: 'Urgente',
};

const PRIORITY_BADGE: Record<MaintenancePriority, string> = {
  low: 'badge-ghost',
  medium: 'badge-info',
  high: 'badge-warning',
  urgent: 'badge-error',
};

const REPORTER_SOURCE_LABEL: Record<string, string> = {
  tenant: 'Inquilino',
  company: 'Inmobiliaria',
};

const ALL_STATUSES: MaintenanceStatus[] = ['open', 'in_progress', 'resolved', 'closed'];
const ALL_PRIORITIES: MaintenancePriority[] = ['low', 'medium', 'high', 'urgent'];

// --- Helpers ---

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
  }).format(amount);
}

// --- Create/Edit Modal ---

interface CreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  properties: PropertyOption[];
  agents: AgentOption[];
  editingRequest?: MaintenanceRequest | null;
}

function CreateEditModal({ isOpen, onClose, properties, agents, editingRequest }: CreateEditModalProps): React.ReactElement | null {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    property_id: editingRequest?.property_id?.toString() || '',
    title: editingRequest?.title || '',
    description: editingRequest?.description || '',
    category: (editingRequest?.category || 'general') as MaintenanceCategory,
    priority: (editingRequest?.priority || 'medium') as MaintenancePriority,
    assigned_to_user_id: editingRequest?.assigned_to_user_id?.toString() || '',
    estimated_cost: editingRequest?.estimated_cost?.toString() || '',
  });

  // Reset form when modal opens with different data
  const resetForm = (): void => {
    setForm({
      property_id: editingRequest?.property_id?.toString() || '',
      title: editingRequest?.title || '',
      description: editingRequest?.description || '',
      category: (editingRequest?.category || 'general') as MaintenanceCategory,
      priority: (editingRequest?.priority || 'medium') as MaintenancePriority,
      assigned_to_user_id: editingRequest?.assigned_to_user_id?.toString() || '',
      estimated_cost: editingRequest?.estimated_cost?.toString() || '',
    });
  };

  const handleSubmit = async (): Promise<void> => {
    if (!form.title.trim() || !form.description.trim()) {
      addToast('Titulo y descripcion son requeridos', 'warning');
      return;
    }

    if (!editingRequest && !form.property_id) {
      addToast('Selecciona una propiedad', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingRequest) {
        // PATCH existing
        const body: Record<string, unknown> = {
          priority: form.priority,
          estimated_cost: form.estimated_cost ? Number(form.estimated_cost) : null,
        };
        if (form.assigned_to_user_id) {
          body.assigned_to_user_id = Number(form.assigned_to_user_id);
        }
        await api.patch(`/v1/maintenance/${editingRequest.id}`, body);
        addToast('Solicitud actualizada', 'success');
      } else {
        // POST new
        const body = {
          property_id: Number(form.property_id),
          title: form.title.trim(),
          description: form.description.trim(),
          category: form.category,
          priority: form.priority,
        };
        await api.post('/v1/maintenance', body);
        addToast('Solicitud creada exitosamente', 'success');
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      onClose();
      resetForm();
    } catch {
      addToast('Error al guardar la solicitud', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-lg">
        <h3 className="font-bold text-lg mb-4">
          {editingRequest ? 'Editar solicitud' : 'Nueva solicitud de mantenimiento'}
        </h3>

        <div className="space-y-4">
          {/* Property select (only on create) */}
          {!editingRequest && (
            <div className="form-control">
              <label className="label">
                <span className="label-text">Propiedad</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={form.property_id}
                onChange={(e) => setForm((f) => ({ ...f, property_id: e.target.value }))}
              >
                <option value="">Seleccionar propiedad...</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Titulo *</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              placeholder="Ej: Fuga de agua en baño"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              disabled={!!editingRequest}
            />
          </div>

          {/* Description */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Descripcion *</span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full"
              rows={3}
              placeholder="Describe el problema en detalle..."
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              disabled={!!editingRequest}
            />
          </div>

          {/* Category (only on create) */}
          {!editingRequest && (
            <div className="form-control">
              <label className="label">
                <span className="label-text">Categoria</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as MaintenanceCategory }))}
              >
                {CATEGORY_OPTIONS.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABEL[cat]}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Priority */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Prioridad</span>
            </label>
            <select
              className="select select-bordered w-full"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value as MaintenancePriority }))}
            >
              {ALL_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABEL[p]}
                </option>
              ))}
            </select>
          </div>

          {/* Assigned to */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Asignar a</span>
            </label>
            <select
              className="select select-bordered w-full"
              value={form.assigned_to_user_id}
              onChange={(e) => setForm((f) => ({ ...f, assigned_to_user_id: e.target.value }))}
            >
              <option value="">Sin asignar</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.first_name} {a.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Estimated cost */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Costo estimado</span>
            </label>
            <input
              type="number"
              className="input input-bordered w-full"
              placeholder="0"
              value={form.estimated_cost}
              onChange={(e) => setForm((f) => ({ ...f, estimated_cost: e.target.value }))}
            />
          </div>
        </div>

        <div className="modal-action">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <span className="loading loading-spinner loading-sm" /> : editingRequest ? 'Guardar cambios' : 'Crear solicitud'}
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>close</button>
      </form>
    </dialog>
  );
}

// --- Detail/Update Modal ---

interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: MaintenanceRequest | null;
}

function DetailModal({ isOpen, onClose, request }: DetailModalProps): React.ReactElement | null {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState(request?.resolution_notes || '');
  const [actualCost, setActualCost] = useState(request?.actual_cost?.toString() || '');

  const handleStatusChange = async (newStatus: MaintenanceStatus): Promise<void> => {
    if (!request) return;

    setIsSubmitting(true);
    try {
      const body: Record<string, unknown> = { status: newStatus };
      if (newStatus === 'resolved' || newStatus === 'closed') {
        if (resolutionNotes.trim()) body.resolution_notes = resolutionNotes.trim();
        if (actualCost) body.actual_cost = Number(actualCost);
      }
      await api.patch(`/v1/maintenance/${request.id}`, body);
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      addToast(`Estado actualizado a "${STATUS_LABEL[newStatus]}"`, 'success');
      onClose();
    } catch {
      addToast('Error al actualizar el estado', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !request) return null;

  return (
    <dialog className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <h3 className="font-bold text-lg mb-1">{request.title}</h3>
        <p className="text-sm text-base-content/60 mb-4">
          {request.property_title} &middot; Creado: {formatDate(request.created_at)}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <span className="text-xs text-base-content/50 uppercase">Estado</span>
            <div className="mt-1">
              <span className={`badge ${STATUS_BADGE[request.status]}`}>
                {STATUS_LABEL[request.status]}
              </span>
            </div>
          </div>
          <div>
            <span className="text-xs text-base-content/50 uppercase">Prioridad</span>
            <div className="mt-1">
              <span className={`badge ${PRIORITY_BADGE[request.priority]}`}>
                {PRIORITY_LABEL[request.priority]}
              </span>
            </div>
          </div>
          <div>
            <span className="text-xs text-base-content/50 uppercase">Categoria</span>
            <p className="mt-1">{CATEGORY_LABEL[request.category]}</p>
          </div>
          <div>
            <span className="text-xs text-base-content/50 uppercase">Reportado por</span>
            <p className="mt-1">
              {request.reporter_name || '-'}
              {request.reporter_source && (
                <span className="text-xs text-base-content/50 ml-1">
                  ({REPORTER_SOURCE_LABEL[request.reporter_source] || request.reporter_source})
                </span>
              )}
            </p>
          </div>
          {request.assigned_to_name && (
            <div>
              <span className="text-xs text-base-content/50 uppercase">Asignado a</span>
              <p className="mt-1">{request.assigned_to_name}</p>
            </div>
          )}
          {request.estimated_cost != null && (
            <div>
              <span className="text-xs text-base-content/50 uppercase">Costo estimado</span>
              <p className="mt-1">{formatCurrency(request.estimated_cost)}</p>
            </div>
          )}
          {request.actual_cost != null && (
            <div>
              <span className="text-xs text-base-content/50 uppercase">Costo real</span>
              <p className="mt-1">{formatCurrency(request.actual_cost)}</p>
            </div>
          )}
        </div>

        <div className="mb-4">
          <span className="text-xs text-base-content/50 uppercase">Descripcion</span>
          <p className="mt-1 whitespace-pre-wrap text-sm">{request.description}</p>
        </div>

        {request.resolution_notes && (
          <div className="mb-4">
            <span className="text-xs text-base-content/50 uppercase">Notas de resolucion</span>
            <p className="mt-1 whitespace-pre-wrap text-sm bg-base-200 rounded p-3">
              {request.resolution_notes}
            </p>
          </div>
        )}

        {/* Update section - only if not closed */}
        {request.status !== 'closed' && (
          <div className="border-t border-base-300 pt-4 mt-4 space-y-3">
            <h4 className="font-semibold text-sm">Actualizar estado</h4>

            {/* Resolution notes */}
            <div className="form-control">
              <label className="label">
                <span className="label-text text-sm">Notas de resolucion</span>
              </label>
              <textarea
                className="textarea textarea-bordered w-full"
                rows={2}
                placeholder="Agregar notas..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
              />
            </div>

            {/* Actual cost */}
            <div className="form-control">
              <label className="label">
                <span className="label-text text-sm">Costo real</span>
              </label>
              <input
                type="number"
                className="input input-bordered input-sm w-full"
                placeholder="0"
                value={actualCost}
                onChange={(e) => setActualCost(e.target.value)}
              />
            </div>

            {/* Status transition buttons */}
            <div className="flex flex-wrap gap-2">
              {request.status === 'open' && (
                <button
                  className="btn btn-sm btn-info"
                  onClick={() => handleStatusChange('in_progress')}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <span className="loading loading-spinner loading-xs" /> : 'En progreso'}
                </button>
              )}
              {(request.status === 'open' || request.status === 'in_progress') && (
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => handleStatusChange('resolved')}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <span className="loading loading-spinner loading-xs" /> : 'Resolver'}
                </button>
              )}
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => handleStatusChange('closed')}
                disabled={isSubmitting}
              >
                {isSubmitting ? <span className="loading loading-spinner loading-xs" /> : 'Cerrar'}
              </button>
            </div>
          </div>
        )}

        <div className="modal-action">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={onClose}>close</button>
      </form>
    </dialog>
  );
}

// --- Main Component ---

export default function MaintenancePage(): React.ReactElement {
  const [statusFilter, setStatusFilter] = useState<MaintenanceStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<MaintenancePriority | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState<MaintenanceRequest | null>(null);
  const [detailRequest, setDetailRequest] = useState<MaintenanceRequest | null>(null);

  // Build query params
  const params = new URLSearchParams();
  if (statusFilter) params.set('status', statusFilter);
  if (priorityFilter) params.set('priority', priorityFilter);
  params.set('page', page.toString());
  params.set('page_size', pageSize.toString());

  const { data: maintenanceRes, isLoading } = useQuery({
    queryKey: ['maintenance', statusFilter, priorityFilter, page],
    queryFn: () => api.get<MaintenanceListResponse>(`/v1/maintenance?${params.toString()}`),
  });

  const { data: propertiesRes } = useQuery({
    queryKey: ['properties-list-simple'],
    queryFn: () => api.get<PropertiesResponse>('/v1/properties'),
  });

  const { data: agentsRes } = useQuery({
    queryKey: ['agents-list-simple'],
    queryFn: () => api.get<AgentsResponse>('/v1/users?role=agent'),
  });

  const requests = maintenanceRes?.data ?? [];
  const meta = maintenanceRes?.meta;
  const properties = propertiesRes?.data ?? [];
  const agents = agentsRes?.data ?? [];

  // Client-side search filter
  const filteredRequests = search.trim()
    ? requests.filter((r) =>
        r.title.toLowerCase().includes(search.toLowerCase())
      )
    : requests;

  const totalPages = meta ? Math.ceil(meta.total / pageSize) : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold">Mantenimiento y refacciones</h1>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => {
            setEditingRequest(null);
            setShowCreateModal(true);
          }}
        >
          + Nueva solicitud
        </button>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Status filter */}
        <select
          className="select select-bordered select-sm w-full sm:w-auto"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as MaintenanceStatus | '');
            setPage(1);
          }}
        >
          <option value="">Todas los estados</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>

        {/* Priority filter */}
        <select
          className="select select-bordered select-sm w-full sm:w-auto"
          value={priorityFilter}
          onChange={(e) => {
            setPriorityFilter(e.target.value as MaintenancePriority | '');
            setPage(1);
          }}
        >
          <option value="">Todas las prioridades</option>
          {ALL_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {PRIORITY_LABEL[p]}
            </option>
          ))}
        </select>

        {/* Search */}
        <input
          type="text"
          className="input input-bordered input-sm w-full sm:w-64"
          placeholder="Buscar por titulo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <span className="loading loading-spinner loading-lg" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredRequests.length === 0 && (
        <div className="text-center py-12 text-base-content/60">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1" stroke="currentColor" className="w-12 h-12 mx-auto mb-3 opacity-40">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
          </svg>
          <p>No se encontraron solicitudes de mantenimiento.</p>
        </div>
      )}

      {/* Cards/Table - Responsive */}
      {!isLoading && filteredRequests.length > 0 && (
        <>
          {/* Mobile cards */}
          <div className="grid grid-cols-1 gap-3 sm:hidden">
            {filteredRequests.map((req) => (
              <div
                key={req.id}
                className="card bg-base-100 shadow cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setDetailRequest(req)}
              >
                <div className="card-body p-4">
                  <div className="flex justify-between items-start">
                    <h3 className="font-medium text-sm">{req.title}</h3>
                    <span className={`badge badge-sm ${PRIORITY_BADGE[req.priority]}`}>
                      {PRIORITY_LABEL[req.priority]}
                    </span>
                  </div>
                  <p className="text-xs text-base-content/60">{req.property_title}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`badge badge-sm ${STATUS_BADGE[req.status]}`}>
                      {STATUS_LABEL[req.status]}
                    </span>
                    <span className="badge badge-sm badge-outline">
                      {CATEGORY_LABEL[req.category]}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2 text-xs text-base-content/50">
                    <span>
                      {req.reporter_name}
                      {req.reporter_source && ` (${REPORTER_SOURCE_LABEL[req.reporter_source] || req.reporter_source})`}
                    </span>
                    <span>{formatDate(req.created_at)}</span>
                  </div>
                  {(req.estimated_cost != null || req.actual_cost != null) && (
                    <div className="text-xs text-base-content/60 mt-1">
                      {req.estimated_cost != null && <span>Est: {formatCurrency(req.estimated_cost)}</span>}
                      {req.actual_cost != null && <span className="ml-2">Real: {formatCurrency(req.actual_cost)}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Titulo</th>
                  <th>Propiedad</th>
                  <th>Categoria</th>
                  <th>Prioridad</th>
                  <th>Estado</th>
                  <th>Reportado por</th>
                  <th>Costo</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((req) => (
                  <tr key={req.id} className="hover cursor-pointer" onClick={() => setDetailRequest(req)}>
                    <td className="font-medium max-w-[200px] truncate">{req.title}</td>
                    <td className="max-w-[150px] truncate">{req.property_title}</td>
                    <td>
                      <span className="badge badge-sm badge-outline">
                        {CATEGORY_LABEL[req.category]}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-sm ${PRIORITY_BADGE[req.priority]}`}>
                        {PRIORITY_LABEL[req.priority]}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-sm ${STATUS_BADGE[req.status]}`}>
                        {STATUS_LABEL[req.status]}
                      </span>
                    </td>
                    <td className="text-sm">
                      {req.reporter_name || '-'}
                      {req.reporter_source && (
                        <span className="text-xs text-base-content/50 block">
                          {REPORTER_SOURCE_LABEL[req.reporter_source] || req.reporter_source}
                        </span>
                      )}
                    </td>
                    <td className="text-sm">
                      {req.estimated_cost != null ? formatCurrency(req.estimated_cost) : '-'}
                      {req.actual_cost != null && (
                        <span className="text-xs text-base-content/50 block">
                          Real: {formatCurrency(req.actual_cost)}
                        </span>
                      )}
                    </td>
                    <td className="text-sm">{formatDate(req.created_at)}</td>
                    <td>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => setDetailRequest(req)}
                        >
                          Ver
                        </button>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => {
                            setEditingRequest(req);
                            setShowCreateModal(true);
                          }}
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center">
              <div className="join">
                <button
                  className="join-item btn btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  «
                </button>
                <button className="join-item btn btn-sm">
                  Pagina {page} de {totalPages}
                </button>
                <button
                  className="join-item btn btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  »
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <CreateEditModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingRequest(null);
        }}
        properties={properties}
        agents={agents}
        editingRequest={editingRequest}
      />

      {/* Detail Modal */}
      <DetailModal
        isOpen={!!detailRequest}
        onClose={() => setDetailRequest(null)}
        request={detailRequest}
      />
    </div>
  );
}
